'use client'

import {
  Box,
  Flex,
  Text,
  IconButton,
  Button,
  Stack,
  Collapse,
  Popover,
  PopoverTrigger,
  useColorModeValue,
  useBreakpointValue,
  useDisclosure,
  Link
} from '@chakra-ui/react'
import {
  HamburgerIcon,
  CloseIcon
} from '@chakra-ui/icons'
import PropTypes from 'prop-types'
import ColorModeSwitcher from './ColorModeSwitch'
import { FaShoppingCart } from 'react-icons/fa';
import CartDrawer from './CartDrawer'; // Adjust path if needed
import useLogout from '../hooks/useLogout';



export default function WithSubnavigation({ isLoggedIn, setIsLoggedIn }) {
  // For mobile nav menu
  const {
    isOpen: isMenuOpen,
    onToggle: onMenuToggle
  } = useDisclosure();

  // For cart drawer
  const {
    isOpen: isCartOpen,
    onOpen: openCart,
    onClose: closeCart
  } = useDisclosure();
  
  // ✅ Use the hook and pass down the setter
  const handleLogout = useLogout(setIsLoggedIn);


  return (
    <Box 
      position={'sticky'} // Added this line to make the navbar sticky
      top={0} // Ensure the navbar sticks to the top
      zIndex={2000} // Ensures the navbar stays on top of other content
      bg={useColorModeValue('white', 'black')}
      color={useColorModeValue('gray.600', 'white')}
    >
    <Flex

        bg={useColorModeValue('white', 'dark.800')}
        color={useColorModeValue('gray.600', 'white')}
        minH={'60px'}
        py={{ base: 2 }}
        px={{ base: 4 }}
        borderBottom={1}
        borderStyle={'solid'}
        borderColor={useColorModeValue('gray.200', 'gray.900')}
        align={'center'}
        >
        <Flex
          flex={{ base: 1, md: 'auto' }}
          ml={{ base: -2 }}
          display={{ base: 'flex', md: 'none' }}>
          <IconButton
            onClick={onMenuToggle}
            icon={isMenuOpen ? <CloseIcon w={3} h={3} /> : <HamburgerIcon w={5} h={5} />}
            variant={'ghost'}
            aria-label={'Toggle Navigation'}
          />
        </Flex>
        <Flex flex={{ base: 1 }} justify={{ base: 'center', md: 'start' }}>
          <Text
            textAlign={useBreakpointValue({ base: 'center', md: 'left' })}
            fontFamily={'heading'}
            fontWeight={800}
            fontSize='2xl'
            bgClip='text'
            bgGradient='linear(to-r, #FF0080, #7928CA)'
            >
              <Link href='/'>
                Papi
              </Link>
          </Text>

          <Flex display={{ base: 'none', md: 'flex' }} ml={10}>
            <DesktopNav isLoggedIn={isLoggedIn} />
          </Flex>
        </Flex>

        <Stack
          flex={{ base: 1, md: 0 }}
          justify={'flex-end'}
          direction={'row'}
          spacing={6}>
          <ColorModeSwitcher />

          {isLoggedIn ? (
            <>
              <IconButton
                icon={<FaShoppingCart />}
                variant="ghost"
                aria-label="View cart"
                onClick={openCart}
              />
              <CartDrawer isOpen={isCartOpen} onClose={closeCart} />
            </>
          ) : (
            <>
              <Button as={'a'} fontSize={'sm'} fontWeight={400} variant={'link'} href={'/login'}>
                Sign In
              </Button>
              <Button
                as={'a'}
                display={{ base: 'none', md: 'inline-flex' }}
                fontSize={'sm'}
                fontWeight={600}
                color={'white'}
                bg={'pink.400'}
                bgGradient={'linear(to-t, #7928CA, #FF0080)'}
                href={'/onboarding'}
                _hover={{
                  bg: 'pink.300',
                  bgGradient: 'linear(to-r, red.500, purple.500)'
                }}>
                Sign Up
              </Button>
            </>
          )}
        </Stack>

      </Flex>

      <Collapse in={isMenuOpen} animateOpacity>
        <MobileNav isLoggedIn={isLoggedIn} handleLogout={handleLogout} />
      </Collapse>
    </Box>
  )
}

// Desktop navigation
const DesktopNav = ({ isLoggedIn, handleLogout }) => {
  const linkColor = useColorModeValue('gray.600', 'gray.200')
  const linkHoverColor = useColorModeValue('gray.800', 'white')

  // Conditionally set NAV_ITEMS based on isLoggedIn
  const NAV_ITEMS = isLoggedIn ? [
    { label: 'Search', href: '/search' },
    { label: 'Bookings', href: '/bookings' },
    { label: 'Affiliate', href: '/affiliate' },
    { label: 'Account', href: '/user' },
    { label: 'Log out', href: '#', onClick: handleLogout }, 
    //{ label: 'Blogs', href: '/blogs' },

  ] : [
    //{ label: 'About', href: '/about' },
    //{ label: 'Blogs', href: '/blogs' },
    { label: 'FAQs', href: '/terms' },
  ];

  return (
    <Stack direction={'row'} spacing={4} alignItems={'center'}>
      {NAV_ITEMS.map((navItem) => (
        <Box key={navItem.label}>
          <Popover trigger={'hover'} placement={'bottom-start'}>
            <PopoverTrigger>
              <Box
                as="a"
                p={2}
                href={navItem.href ?? '#'}
                fontSize={'m'}
                fontWeight={500}
                color={linkColor}
                _hover={{
                  textDecoration: 'none',
                  color: linkHoverColor,
                }}>
                {navItem.label}
              </Box>
            </PopoverTrigger>
          </Popover>
        </Box>
      ))}
    </Stack>
  )
}

// Mobile navigation
const MobileNav = ({ isLoggedIn, handleLogout }) => {
  // Conditionally set NAV_ITEMS based on isLoggedIn
  const NAV_ITEMS = isLoggedIn ? [
    { label: 'Search', href: '/search' },
    { label: 'Bookings', href: '/bookings' },
    { label: 'Affiliate', href: '/affiliate' }, 
    { label: 'Account', href: '/user' },
    //{ label: 'Blogs', href: '/blogs' },
    { label: 'Log out', href: '#', onClick: handleLogout }, // Add logout for mobile
  ] : [
    //{ label: 'About', href: '/about' },
    //{ label: 'Blogs', href: '/blogs' },
    { label: `FAQ's`, href: '/terms' },
    { label: 'Sign Up', href: '/onboarding' }
  ];

  return (
    <Stack bg={useColorModeValue('white', 'dark.800')} p={4} display={{ md: 'none' }}>
      {NAV_ITEMS.map((navItem) => (
        <MobileNavItem key={navItem.label} {...navItem} />
      ))}
    </Stack>
  )
}

const MobileNavItem = ({ label, href, onClick }) => {
  return (
    <Stack spacing={4}>
      <Box
        py={2}
        as="a"
        href={href ?? '#'}
        justifyContent="space-between"
        alignItems="center"
        _hover={{
          textDecoration: 'none',
        }}
        onClick={onClick} // Call the onClick function if provided (for logout)
      >
        <Text fontWeight={600} color={useColorModeValue('gray.600', 'gray.200')}>
          {label}
        </Text>
      </Box>
    </Stack>
  )
}

WithSubnavigation.propTypes = {
  isLoggedIn: PropTypes.bool.isRequired,
  setIsLoggedIn: PropTypes.func.isRequired, // ✅ updated
};
